import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import generalOptionSets from "../../options/general.json";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldLabel,
  getFieldOptions,
  getFieldValue,
  getFormsySubmissionsForClient,
  getSubmission,
  handleCopyUrl,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
  getLetterheadLogoDataURL,
  getYearRangeAsArray,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormInput,
  FormRadioButton,
  FormSelect,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import getAge from "../services/ageService";
import {
  getFormsySubmissionsByFormIdForClient,
  getMedications,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function SCTFCF374() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadStatus, setLoadStatus] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram.value
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram.value = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!getFieldValue(dataObj.SelectedProgram)) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [incidentReports, setIncidentReports] = useState([]);

  useEffect(() => {
    const loadReports = async () => {
      const response = await getFormsySubmissionsByFormIdForClient(
        "94F447D5-3278-EE11-826A-0050F21C6F69", // SC FRSCP Fast Assessment Form Container Id
        clientid
      );

      if (!isMountedRef.current) {
        return;
      }

      if (response) {
        const result = JSON.parse(response);

        if (result?.FormSubmissions?.Submission?.length > 0) {
          setIncidentReports(
            result.FormSubmissions.Submission.sort((a, b) => {
              const aDate = moment(
                getFieldValue(a.FormResults.results.IncidentDate)
              );
              const bDate = moment(
                getFieldValue(b.FormResults.results.IncidentDate)
              );

              return aDate.isBefore(bDate) ? 1 : bDate.isBefore(aDate) ? -1 : 0;
            })
          );
        }
      }
    };

    loadReports();
  }, [clientid]);

  useEffect(() => {
    if (!loadStatus.dataSet || !clientid || data.CurrentMedications?.value) {
      return;
    }

    const loadMedications = async () => {
      const response = await getMedications(clientid);

      if (!isMountedRef.current) {
        return;
      }

      if (response) {
        const today = moment();
        const currentMedications = response.filter((med) => {
          const startDate = moment(getFieldDate(med.ccx_startdate));

          if (startDate.isAfter(today)) {
            return false;
          }

          if (med.ccx_stopdate === undefined) {
            return true;
          }

          const endDate = moment(getFieldDate(med.ccx_stopdate));

          if (endDate.isBefore(today)) {
            return false;
          }

          return true;
        });

        setData((d) => {
          const obj = { ...d };
          obj.CurrentMedications.value = currentMedications
            .map((med) => med.ccx_brandname)
            .join(", ");
          return obj;
        });
      }
    };

    loadMedications();
  }, [clientid, loadStatus.dataSet, data.CurrentMedications?.value]);

  const handleDateChange = (e) => {
    handleChange(e);
    processIncidentReports();
  };

  const processIncidentReports = () => {
    setData((d) => {
      const obj = { ...d };

      if (!getFieldValue(obj.Month) || !getFieldValue(obj.Year)) {
        return obj;
      }

      const startDate = moment(
        `${getFieldValue(obj.Month)}/1/${getFieldValue(obj.Year)}`
      );
      const endDate = moment(
        `${getFieldValue(obj.Month)}/1/${getFieldValue(obj.Year)}`
      ).endOf("month");

      if (endDate.isBefore(startDate)) {
        return obj;
      }

      const monthlyReports = incidentReports.filter((ir) => {
        const incidentDate = moment(
          getFieldValue(ir.FormResults.results.IncidentDate)
        );

        return (
          incidentDate.isSameOrAfter(startDate) &&
          incidentDate.isSameOrBefore(endDate)
        );
      });

      if (monthlyReports.length === 0) return obj;

      obj.CriticalIncident.value = "Yes";

      const incidentObj = {
        AWOL: [],
        AttemptedSuicide: [],
        EmergencyPlacement: [],
        EmergencyAssessment: [],
        EmergencyTreatment: [],
        WeaponPossession: [],
        SubstancePossession: [],
        SchoolRemoval: [],
        OutsideAgency: [],
      };

      const otherIssues = [];

      monthlyReports.forEach((ir) => {
        const typeOfIncident = getFieldValue(
          ir.FormResults.results.TypeOfIncident
        );
        const incidentDescription = getFieldValue(
          ir.FormResults.results.IncidentDescription
        );
        const incidentFollowup = getFieldValue(
          ir.FormResults.results.FollowUpActions
        );

        switch (typeOfIncident) {
          case "Absence without leave/Runaway":
            incidentObj.AWOL.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Attempted suicide by client":
            incidentObj.AttemptedSuicide.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Emergency change in placement:1":
          case "Emergency change in placement:2":
            incidentObj.EmergencyPlacement.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Off-site emergency medical assessment":
            incidentObj.EmergencyAssessment.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Off-site emergency medical treatment":
            incidentObj.EmergencyTreatment.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Possession of a weapon":
            incidentObj.WeaponPossession.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Possession of an illegal substance":
            incidentObj.SubstancePossession.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Removal from school:":
            incidentObj.SchoolRemoval.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Report/involvement of an outside regulatory agency":
            incidentObj.OutsideAgency.push({
              Date: moment(
                getFieldValue(ir.FormResults.results.IncidentDate)
              ).format("L"),
              Details: `${incidentDescription}
            
${incidentFollowup}`,
            });
            break;
          case "Death of client":
          case "Any serious injury or illness":
          case "Suicidal gesture, not life threatening":
          case "Prescription medication error":
          case "Placement in seclusion or restraints":
          case "Attempt to contact prohibited persons and/or contact with person that suggest the potential child/youth has been a victim of sex trafficking":
          default:
            otherIssues.push(`Type: ${typeOfIncident}
Description: ${incidentDescription}
Follow-up: ${incidentFollowup}`);
            break;
        }
      });

      for (var group in incidentObj) {
        if (incidentObj[group].length > 0) {
          obj[`${group}Dates`].value = incidentObj[group]
            .map((group) => group.Date)
            .join(", ");
          obj[`${group}Details`].value = incidentObj[group].map(
            (group) => group.Details
          ).join(`

`);
        }
      }
      return obj;
    });
  };

  const getClientInfo = () => {
    if (clientData.client) {
      let clientInfo = `${clientData.client.fullName}`;
      if (clientData.client.mpi) clientInfo += ` - ${clientData.client.mpi}`;
      if (clientData.client.sex)
        clientInfo += ` - ${getFieldName(clientData.client.sex)}`;
      if (clientData.client.dob)
        clientInfo += ` - ${getFieldDate(clientData.client.dob)} - ${getAge(
          getFieldText(clientData.client.dob),
          getFieldText(clientData.client.deceaseddate)
        )}`;

      return clientInfo;
    }
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          letterhead={
            <>
              <img
                src={getLetterheadLogoDataURL()}
                alt="safy logo"
                style={{ width: "200px", height: "auto" }}
              ></img>
              <div className="d-none d-print-inline pt-2 rounded-top">
                <div className="h3">
                  F-374 Traditional Foster Care Monthly Progress Summary
                </div>
                <div>{getClientInfo()}</div>
              </div>
            </>
          }
          hideHeaderOnPrint={true}
        />
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration?.[0]
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        {formsyDetails.ClientProgramName}
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="Month"
                    label={getFieldLabel(formConfig.Month)}
                    error={errors.Month}
                    value={getFieldValue(data.Month)}
                    options={generalOptionSets.month}
                    onChange={handleDateChange}
                    identical={compare("Month")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="Year"
                    label={getFieldLabel(formConfig.Year)}
                    error={errors.Year}
                    value={getFieldValue(data.Year)}
                    options={getYearRangeAsArray(2022)
                      .sort((a, b) => b - a)
                      .map((year) => {
                        const yearStr = `${year}`;
                        return { _id: yearStr, name: yearStr };
                      })}
                    onChange={handleDateChange}
                    identical={compare("Year")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  Date of Admission:
                  <br />
                  {moment(
                    getFieldDate(
                      clientData.programs?.find(
                        (program) =>
                          program.ccx_client_programid ===
                          getFieldValue(data.SelectedProgram)
                      )?.ccx_admitdate
                    )
                  ).format("LL")}
                </div>
                <div className="col">
                  <FormInput
                    name="DateOfDischarge"
                    label={getFieldLabel(formConfig.DateOfDischarge)}
                    error={errors.DateOfDischarge}
                    value={getFieldValue(data.DateOfDischarge)}
                    onChange={handleChange}
                    identical={compare("DateOfDischarge")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <div className="container border-top border-start">
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    {getFieldLabel(formConfig.ContactWithYouth)}
                  </div>
                  <div className="col border-end py-2">
                    {getFieldLabel(formConfig.PhoneContactWithFP)}
                  </div>
                  <div className="col border-end py-2">
                    {getFieldLabel(formConfig.ContactWithFP)}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormInput
                      name="ContactWithYouth"
                      error={errors.ContactWithYouth}
                      value={getFieldValue(data.ContactWithYouth)}
                      onChange={handleChange}
                      identical={compare("ContactWithYouth")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="PhoneContactWithFP"
                      error={errors.PhoneContactWithFP}
                      value={getFieldValue(data.PhoneContactWithFP)}
                      onChange={handleChange}
                      identical={compare("PhoneContactWithFP")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="ContactWithFP"
                      error={errors.ContactWithFP}
                      value={getFieldValue(data.ContactWithFP)}
                      onChange={handleChange}
                      identical={compare("ContactWithFP")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <FormRadioButton
                name="PermanencyActivity"
                label={getFieldLabel(formConfig.PermanencyActivity)}
                error={errors.PermanencyActivity}
                value={getFieldValue(data.PermanencyActivity)}
                options={getFieldOptions(data.PermanencyActivity)}
                onChange={handleChange}
                identical={compare("PermanencyActivity")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.PermanencyActivity) === "Yes" ? (
                <FormInput
                  name="PermanencyActivityDetails"
                  label={getFieldLabel(formConfig.PermanencyActivityDetails)}
                  error={errors.PermanencyActivityDetails}
                  value={getFieldValue(data.PermanencyActivityDetails)}
                  onChange={handleChange}
                  identical={compare("PermanencyActivityDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <FormRadioButton
                name="AgeAppropriateBehavior"
                label={getFieldLabel(formConfig.AgeAppropriateBehavior)}
                error={errors.AgeAppropriateBehavior}
                value={getFieldValue(data.AgeAppropriateBehavior)}
                options={getFieldOptions(data.AgeAppropriateBehavior)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.AgeAppropriateBehavior) === "No" ? (
                <FormInput
                  name="AgeAppropriateBehaviorDetails"
                  label={getFieldLabel(
                    formConfig.AgeAppropriateBehaviorDetails
                  )}
                  error={errors.AgeAppropriateBehaviorDetails}
                  value={getFieldValue(data.AgeAppropriateBehaviorDetails)}
                  onChange={handleChange}
                  identical={compare("AgeAppropriateBehaviorDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <FormRadioButton
                name="AppropriateInteraction"
                label={getFieldLabel(formConfig.AppropriateInteraction)}
                error={errors.AppropriateInteraction}
                value={getFieldValue(data.AppropriateInteraction)}
                options={getFieldOptions(data.AppropriateInteraction)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.AppropriateInteraction) === "No" ? (
                <FormInput
                  name="AppropriateInteractionDetails"
                  label={getFieldLabel(
                    formConfig.AppropriateInteractionDetails
                  )}
                  error={errors.AppropriateInteractionDetails}
                  value={getFieldValue(data.AppropriateInteractionDetails)}
                  onChange={handleChange}
                  identical={compare("AppropriateInteractionDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <FormRadioButton
                name="IndependentLivingAge"
                label={getFieldLabel(formConfig.IndependentLivingAge)}
                error={errors.IndependentLivingAge}
                value={getFieldValue(data.IndependentLivingAge)}
                options={getFieldOptions(data.IndependentLivingAge)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.IndependentLivingAge) === "Yes" ? (
                <FormInput
                  name="ILGoal"
                  label={getFieldLabel(formConfig.ILGoal)}
                  error={errors.ILGoal}
                  value={getFieldValue(data.ILGoal)}
                  onChange={handleChange}
                  identical={compare("ILGoal")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <FormRadioButton
                name="StablePlacement"
                label={getFieldLabel(formConfig.StablePlacement)}
                error={errors.StablePlacement}
                value={getFieldValue(data.StablePlacement)}
                options={getFieldOptions(data.StablePlacement)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="RiskOfDisruption"
                label={getFieldLabel(formConfig.RiskOfDisruption)}
                error={errors.RiskOfDisruption}
                value={getFieldValue(data.RiskOfDisruption)}
                onChange={handleChange}
                identical={compare("RiskOfDisruption")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h6">
                {getFieldLabel(formConfig.WellbeingHeader)}
              </div>
              <FormRadioButton
                name="HealthConcerns"
                label={getFieldLabel(formConfig.HealthConcerns)}
                error={errors.HealthConcerns}
                value={getFieldValue(data.HealthConcerns)}
                options={getFieldOptions(data.HealthConcerns)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.HealthConcerns) === "Yes" ? (
                <FormInput
                  name="HealthConcernsDetails"
                  label={getFieldLabel(formConfig.HealthConcernsDetails)}
                  error={errors.HealthConcernsDetails}
                  value={getFieldValue(data.HealthConcernsDetails)}
                  onChange={handleChange}
                  identical={compare("HealthConcernsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <FormRadioButton
                name="TherapyAppointments"
                label={getFieldLabel(formConfig.TherapyAppointments)}
                error={errors.TherapyAppointments}
                value={getFieldValue(data.TherapyAppointments)}
                options={getFieldOptions(data.TherapyAppointments)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormRadioButton
                name="DoctorVisits"
                label={getFieldLabel(formConfig.DoctorVisits)}
                error={errors.DoctorVisits}
                value={getFieldValue(data.DoctorVisits)}
                options={getFieldOptions(data.DoctorVisits)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormRadioButton
                name="OtherAppointments"
                label={getFieldLabel(formConfig.OtherAppointments)}
                error={errors.OtherAppointments}
                value={getFieldValue(data.OtherAppointments)}
                options={getFieldOptions(data.OtherAppointments)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="CurrentMedications"
                label={getFieldLabel(formConfig.CurrentMedications)}
                error={errors.CurrentMedications}
                value={getFieldValue(data.CurrentMedications)}
                onChange={handleChange}
                identical={compare("CurrentMedications")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormRadioButton
                name="CriticalIncident"
                label={getFieldLabel(formConfig.CriticalIncident)}
                error={errors.CriticalIncident}
                value={getFieldValue(data.CriticalIncident)}
                options={getFieldOptions(data.CriticalIncident)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.CriticalIncident) === "Yes" ? (
                <>
                  <div>{getFieldLabel(formConfig.CriticalIncidentLabel)}</div>
                  <div className="container border-top border-start mb-2">
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        Type of Incident
                      </div>
                      <div className="col-3 border-end py-2">Date(s)</div>
                      <div className="col border-end py-2">
                        Details/Outcomes
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.AWOLDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="AWOLDates"
                          error={errors.AWOLDates}
                          value={getFieldValue(data.AWOLDates)}
                          onChange={handleChange}
                          identical={compare("AWOLDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="AWOLDetails"
                          error={errors.AWOLDetails}
                          value={getFieldValue(data.AWOLDetails)}
                          onChange={handleChange}
                          identical={compare("AWOLDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.AttemptedSuicideDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="AttemptedSuicideDates"
                          error={errors.AttemptedSuicideDates}
                          value={getFieldValue(data.AttemptedSuicideDates)}
                          onChange={handleChange}
                          identical={compare("AttemptedSuicideDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="AttemptedSuicideDetails"
                          error={errors.AttemptedSuicideDetails}
                          value={getFieldValue(data.AttemptedSuicideDetails)}
                          onChange={handleChange}
                          identical={compare("AttemptedSuicideDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.EmergencyPlacementDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="EmergencyPlacementDates"
                          error={errors.EmergencyPlacementDates}
                          value={getFieldValue(data.EmergencyPlacementDates)}
                          onChange={handleChange}
                          identical={compare("EmergencyPlacementDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="EmergencyPlacementDetails"
                          error={errors.EmergencyPlacementDetails}
                          value={getFieldValue(data.EmergencyPlacementDetails)}
                          onChange={handleChange}
                          identical={compare("EmergencyPlacementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.EmergencyAssessmentDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="EmergencyAssessmentDates"
                          error={errors.EmergencyAssessmentDates}
                          value={getFieldValue(data.EmergencyAssessmentDates)}
                          onChange={handleChange}
                          identical={compare("EmergencyAssessmentDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="EmergencyAssessmentDetails"
                          error={errors.EmergencyAssessmentDetails}
                          value={getFieldValue(data.EmergencyAssessmentDetails)}
                          onChange={handleChange}
                          identical={compare("EmergencyAssessmentDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.EmergencyTreatmentDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="EmergencyTreatmentDates"
                          error={errors.EmergencyTreatmentDates}
                          value={getFieldValue(data.EmergencyTreatmentDates)}
                          onChange={handleChange}
                          identical={compare("EmergencyTreatmentDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="EmergencyTreatmentDetails"
                          error={errors.EmergencyTreatmentDetails}
                          value={getFieldValue(data.EmergencyTreatmentDetails)}
                          onChange={handleChange}
                          identical={compare("EmergencyTreatmentDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.WeaponPossessionDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="WeaponPossessionDates"
                          error={errors.WeaponPossessionDates}
                          value={getFieldValue(data.WeaponPossessionDates)}
                          onChange={handleChange}
                          identical={compare("WeaponPossessionDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="WeaponPossessionDetails"
                          error={errors.WeaponPossessionDetails}
                          value={getFieldValue(data.WeaponPossessionDetails)}
                          onChange={handleChange}
                          identical={compare("WeaponPossessionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.SubstancePossessionDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="SubstancePossessionDates"
                          error={errors.SubstancePossessionDates}
                          value={getFieldValue(data.SubstancePossessionDates)}
                          onChange={handleChange}
                          identical={compare("SubstancePossessionDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="SubstancePossessionDetails"
                          error={errors.SubstancePossessionDetails}
                          value={getFieldValue(data.SubstancePossessionDetails)}
                          onChange={handleChange}
                          identical={compare("SubstancePossessionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.SchoolRemovalDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="SchoolRemovalDates"
                          error={errors.SchoolRemovalDates}
                          value={getFieldValue(data.SchoolRemovalDates)}
                          onChange={handleChange}
                          identical={compare("SchoolRemovalDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="SchoolRemovalDetails"
                          error={errors.SchoolRemovalDetails}
                          value={getFieldValue(data.SchoolRemovalDetails)}
                          onChange={handleChange}
                          identical={compare("SchoolRemovalDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-4 border-end py-2">
                        {getFieldLabel(formConfig.OutsideAgencyDates)}
                      </div>
                      <div className="col-3 border-end py-2">
                        <FormInput
                          name="OutsideAgencyDates"
                          error={errors.OutsideAgencyDates}
                          value={getFieldValue(data.OutsideAgencyDates)}
                          onChange={handleChange}
                          identical={compare("OutsideAgencyDates")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col border-end py-2">
                        <FormInput
                          name="OutsideAgencyDetails"
                          error={errors.OutsideAgencyDetails}
                          value={getFieldValue(data.OutsideAgencyDetails)}
                          onChange={handleChange}
                          identical={compare("OutsideAgencyDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
              <FormInput
                name="OtherIssues"
                label={getFieldLabel(formConfig.OtherIssues)}
                error={errors.OtherIssues}
                value={getFieldValue(data.OtherIssues)}
                onChange={handleChange}
                identical={compare("OtherIssues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h6">
                {getFieldLabel(formConfig.EducationSection)}
              </div>
              <FormRadioButton
                name="GradeLevel"
                label={getFieldLabel(formConfig.GradeLevel)}
                error={errors.GradeLevel}
                value={getFieldValue(data.GradeLevel)}
                options={getFieldOptions(data.GradeLevel)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormRadioButton
                name="IEP504Plan"
                label={getFieldLabel(formConfig.IEP504Plan)}
                error={errors.IEP504Plan}
                value={getFieldValue(data.IEP504Plan)}
                options={getFieldOptions(data.IEP504Plan)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.IEP504Plan) === "No" ? (
                <FormRadioButton
                  name="IEP504PlanNeeded"
                  label={getFieldLabel(formConfig.IEP504PlanNeeded)}
                  error={errors.IEP504PlanNeeded}
                  value={getFieldValue(data.IEP504PlanNeeded)}
                  options={getFieldOptions(data.IEP504PlanNeeded)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <FormRadioButton
                name="BehavioralIssues"
                label={getFieldLabel(formConfig.BehavioralIssues)}
                error={errors.BehavioralIssues}
                value={getFieldValue(data.BehavioralIssues)}
                options={getFieldOptions(data.BehavioralIssues)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormRadioButton
                name="Expulsion"
                label={getFieldLabel(formConfig.Expulsion)}
                error={errors.Expulsion}
                value={getFieldValue(data.Expulsion)}
                options={getFieldOptions(data.Expulsion)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="OtherInformation"
                label={getFieldLabel(formConfig.OtherInformation)}
                error={errors.OtherInformation}
                value={getFieldValue(data.OtherInformation)}
                onChange={handleChange}
                identical={compare("OtherInformation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCTFCF374;
